import { useCallback, useContext } from 'react';
import styled from '@emotion/styled';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import VolumeUpOutlinedIcon from '@mui/icons-material/VolumeUpOutlined';
import { MenuItem, Select } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { type ChatInteractionMode } from '@trinity/chat/enums';
import { InputControlsContext } from '@trinity/chat-components/components/BrandInsights/context';
import { VoiceModeContext } from '@trinity/chat-components/containers/TextToSpeechAudioPlayer/Context.js';
import { useAvatarPanel } from './AvatarPanel.context';

/**
 * InteractionModeDropdown — replaces the Voice Mode switch in the Digital Twin chat.
 *
 * Options:
 *  - Video (default) — avatar session active, user types text, TTS drives avatar speech.
 *  - Voice           — avatar session active, mic input enabled.
 *  - Text            — avatar session terminated, plain chat without TTS.
 *
 * When showVoiceOption is false (chatVoiceMode feature flag off), only
 * Video and Text are shown.
 *
 * Mode is stored in AvatarPanelContext (owned by DigitalTwinScreen) so that
 * DigitalTwinScreen can conditionally mount/unmount LiveAvatarSessionManager.
 */
export const InteractionModeDropdown = () => {
  const voicemode = useContext(VoiceModeContext);
  const { onClickVoiceMode } = useContext(InputControlsContext);
  const { availableModes, interactionMode, setInteractionMode } = useAvatarPanel();

  const handleChange = useCallback(
    (e: SelectChangeEvent<ChatInteractionMode>) => {
      const next = e.target.value;

      switch (next) {
        case 'video':
          // Video: avatar session active, user types text, TTS drives avatar speech.
          if (voicemode?.enabled) onClickVoiceMode();
          setInteractionMode('video');
          break;
        case 'voice':
          // Voice: avatar session active, mic input enabled.
          if (!voicemode?.enabled) onClickVoiceMode();
          setInteractionMode('voice');
          break;
        case 'text':
          // Text: end avatar session, plain chat without TTS.
          if (voicemode?.enabled) onClickVoiceMode();
          setInteractionMode('text');
          break;
        default:
          break;
      }
    },
    [voicemode?.enabled, onClickVoiceMode, setInteractionMode]
  );

  return (
    <StyledSelect
      value={interactionMode}
      onChange={handleChange}
      variant="outlined"
      size="small"
      renderValue={(value) => <SelectedValue interactionMode={value} />}
    >
      {(availableModes ?? []).map((mode) => {
        const Icon = INTERACTION_MODE_ICONS[mode];
        const label = mode.charAt(0).toUpperCase() + mode.slice(1);
        return (
          <StyledMenuItem key={mode} value={mode}>
            <MenuIcon>
              <Icon fontSize="small" />
            </MenuIcon>
            {label}
          </StyledMenuItem>
        );
      })}
    </StyledSelect>
  );
};

const INTERACTION_MODE_ICONS = {
  video: VideocamOutlinedIcon,
  voice: VolumeUpOutlinedIcon,
  text: ChatBubbleOutlineIcon,
} as const;

const SelectedValue = ({ interactionMode }: { interactionMode: ChatInteractionMode }) => {
  const Icon = INTERACTION_MODE_ICONS[interactionMode];
  const label = interactionMode.charAt(0).toUpperCase() + interactionMode.slice(1);

  return (
    <SelectedContent>
      <Icon fontSize="small" />
      <span>{label}</span>
    </SelectedContent>
  );
};

const StyledSelect = styled(Select<ChatInteractionMode>)({
  height: 28,
  fontSize: 13,
  color: '#555',
  background: 'transparent',

  '& fieldset': {
    borderColor: '#ddd !important',
    borderRadius: 6,
  },

  '&:hover fieldset': {
    borderColor: '#aaa !important',
  },

  '& .MuiSelect-select': {
    padding: '2px 28px 2px 8px !important',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
});

const SelectedContent = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  line-height: 1;
`;

const StyledMenuItem = styled(MenuItem)`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  padding: 6px 14px;
`;

const MenuIcon = styled.span`
  display: flex;
  align-items: center;
  color: #555;
`;
